/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import store from 'js/lib/coursera.store';
import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { Button, Link, breakpoints, typography2 } from '@coursera/cds-core';

import Separator from 'bundles/authentication-common/components/Separator';
import * as actions from 'bundles/authentication-common/components/actions';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { LEARNER_HELP_CENTER_LINK, type ThirdPartyAuthService } from 'bundles/authentication-common/constants';
import type { SuccessResponse } from 'bundles/authentication-common/types';
import usePathname from 'bundles/authentication/shared/hooks/usePathname';
import { shouldShowLegalConsolidationAuthFlow, shouldShowTOSDisclaimer } from 'bundles/authentication/utils';
import HorizontalSocialSSO from 'bundles/expression-of-interest/components/authentication/HorizontalSocialSSO';
import CourseraAndRecaptchaDisclaimer from 'bundles/expression-of-interest/components/xddp/CourseraAndRecaptchaDisclaimer';
import LoginForm from 'bundles/expression-of-interest/components/xddp/LoginForm';
import SignupForm from 'bundles/expression-of-interest/components/xddp/SignupForm';
import { EMBEDDED_DEGREE_FORM_TRACKER } from 'bundles/expression-of-interest/constants';
import useInitialFocusOnSso from 'bundles/expression-of-interest/hooks/useInitialFocusOnSSO';
import useSaveOnboardingOnSignup from 'bundles/onboarding-common/hooks/useSaveOnboardingOnSignup';
import TrackedButton from 'bundles/page/components/TrackedButton';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

type PropsFromCaller = {
  onUserRegistered?: () => void;
  services: ThirdPartyAuthService[];
  extraFormContent?: React.ReactElement;
  formToggled?: () => void;
  slug: string;
  trackingProductId: string;
};

const styles = {
  disclaimer: () => css`
    ${typography2.bodySecondary};
  `,
  withPaddingTop: css`
    padding-top: var(--cds-spacing-200);
  `,
  withPaddingBottom: css`
    padding-bottom: var(--cds-spacing-200);
  `,
  divider: () => css`
    margin: 0 -72px var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      margin: 0 -40px var(--cds-spacing-400);
    }
  `,
  actionsContainer: css`
    padding: 0 24px;
  `,
  formContainer: css`
    margin-bottom: var(--cds-spacing-400);
  `,
};

export const DegreesEmbeddedAuthContent = ({
  onUserRegistered,
  services,
  extraFormContent,
  formToggled,
  slug,
  trackingProductId,
}: PropsFromCaller) => {
  const logEvent = useRetracked();
  const [isLogin, setIsLogin] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const { saveOnboardingOnSignup } = useSaveOnboardingOnSignup();
  const redirectTo = router.location.query.redirectTo || router.location.query.r;
  const { initialFocusOnSSO } = useInitialFocusOnSso([isLogin]);
  const servicesLabel = services.reduce(
    (result, service) => ({ ...result, [service]: service.charAt(0).toUpperCase() + service.slice(1) }),
    {}
  );

  useEffect(() => {
    // Building funnel for sign-ups and EOIs within degrees embedded auth content
    logEvent({
      action: EMBEDDED_DEGREE_FORM_TRACKER.actions.OPEN,
      trackingName: EMBEDDED_DEGREE_FORM_TRACKER.name,
      trackingData: { pathname },
    });
    // Run  on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDefaultError = () => {
    return (
      <FormattedMessage
        message={_t('An unexpected error has occurred. Please try again or contact our {helpCenterLink}.')}
        helpCenterLink={<Link href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</Link>}
      />
    );
  };

  const handleSuccessfulSignup = useCallback<(response: SuccessResponse) => Promise<void>>(
    async (response: SuccessResponse) => {
      Instrumentation.register();
      logEvent({
        action: EMBEDDED_DEGREE_FORM_TRACKER.actions.HANDLE_SUCCESSFUL_SIGNUP,
        trackingName: EMBEDDED_DEGREE_FORM_TRACKER.name,
        trackingData: { pathname },
      });

      await saveOnboardingOnSignup();
      if (onUserRegistered) {
        onUserRegistered();
      } else {
        actions.onSignup(response.redirectTo, { addShowTouAcceptParam: shouldShowLegalConsolidationAuthFlow() });
      }
    },
    [logEvent, onUserRegistered, pathname, saveOnboardingOnSignup]
  );

  const handleSuccessfulLogin = useCallback<(redirectURL?: string) => void>(
    (redirectURL?: string) => {
      Instrumentation.login();
      logEvent({
        action: EMBEDDED_DEGREE_FORM_TRACKER.actions.HANDLE_SUCCESSFUL_LOGIN,
        trackingName: EMBEDDED_DEGREE_FORM_TRACKER.name,
        trackingData: { pathname },
      });
      store.set('has_previously_logged_in', true);
      if (onUserRegistered) {
        onUserRegistered();
      } else {
        actions.onLogin(redirectTo || redirectURL);
      }
    },
    [logEvent, onUserRegistered, redirectTo, pathname]
  );

  const onSocialSSOSuccess = useCallback<() => Promise<void>>(async () => {
    await saveOnboardingOnSignup();
    onUserRegistered?.();
  }, [onUserRegistered, saveOnboardingOnSignup]);

  const onSocialSSOError = useCallback<(error: SSOError) => void>((error: SSOError) => {
    Instrumentation.thirdPartyError(error.service, error);
  }, []);

  const toggleForm = useCallback<() => $TSFixMe>(() => {
    setIsLogin((isLoginLocal) => !isLoginLocal);
    formToggled?.();
  }, [formToggled]);

  return (
    <div className="rc-DegreesEmbeddedSignup">
      <React.Fragment>
        <HorizontalSocialSSO
          services={services}
          servicesLabel={servicesLabel}
          onSuccess={onSocialSSOSuccess}
          onError={onSocialSSOError}
          toFocus={initialFocusOnSSO}
          trackingProductId={trackingProductId}
        />
        <CourseraAndRecaptchaDisclaimer
          hasCourseraDisclaimer={!isLogin && shouldShowTOSDisclaimer()}
          css={[styles.disclaimer, styles.withPaddingTop]}
        />
        <Separator text={_t('or')} />
        <Button
          fullWidth
          variant="ghost"
          data-unit={isLogin ? 'switch-to-signup' : 'switch-to-login'}
          className={`m-b-1 text-xs-center ${isLogin ? 'switchToSignup' : 'switchToLogin'}`}
          onClick={toggleForm}
          component={TrackedButton}
          trackingName={isLogin ? 'switch_to_signup' : 'switch_to_login'}
          withVisibilityTracking={false}
          requireFullyVisible={false}
        >
          {isLogin ? _t('Sign up for Coursera') : _t('Sign in with your account')}
        </Button>
      </React.Fragment>
      <div css={styles.formContainer}>
        {isLogin ? (
          <LoginForm
            onSuccess={handleSuccessfulLogin}
            onForgotPassword={() => {
              window.open('/reset', '_blank');
            }}
            onError={() => getDefaultError()}
            extraFormContent={extraFormContent}
            toFocus={!initialFocusOnSSO}
            showSubmitButton
            trackingProductId={trackingProductId}
          />
        ) : (
          <SignupForm
            onSuccess={handleSuccessfulSignup}
            navigateToLogin={() => setIsLogin(true)}
            extraFormContent={extraFormContent}
            slug={slug}
            toFocus={!initialFocusOnSSO}
            showSubmitButton
            trackingProductId={trackingProductId}
          />
        )}
      </div>
    </div>
  );
};

export default DegreesEmbeddedAuthContent;
